import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { useState } from 'react'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: rpCtrlSalesDO (Lệnh xuất hàng / Delivery Order).
// frmCtrlSalesQryDO btnPrintDO_Click → DoCmd.OpenReport "rpCtrlSalesDO" WHERE
// do_no. PRESENTATIONAL dialog: HTML đến qua props (parent fetch). Mirror
// DeliveryPrintDialog: iframe srcDoc preview + nút Print → printAndUploadReport.

interface DoPrintDialogProps {
  open: boolean
  /** Tiêu đề dialog (vd "Print Preview - DO (1831TNC)"). */
  title: string
  /** HTML preview (đã do parent fetch + buildPreviewHtml). */
  html: string
  /** Đang fetch HTML (overlay spinner). */
  loading: boolean
  canEdit?: boolean
  onClose: () => void
}

// Override CSS preview để fit A4 trong iframe scaled (memory: print fit A4).
// Same set as DeliveryPrintDialog (access-report + .ctl absolute).
const PRINT_PREVIEW_CSS = `
@page { size: A4 !important; margin: 0 !important; }
@media print {
  @page { size: A4 !important; margin: 0 !important; }
  body { margin: 0 !important; }
  .report-page { width: 210mm !important; max-width: none !important; min-height: 297mm !important; padding: 9mm 11mm !important; }
}
@media screen and (max-width: 600px) {
  .report-page { min-height: auto !important; max-width: 100% !important; width: 100% !important; padding: 4mm !important; margin: 0 !important; }
  body { margin: 0 !important; background: #fff !important; }
}
body { margin: 0 !important; }
.print-action { display: none !important; }
.report-page { width: 100% !important; max-width: 200mm !important; padding: 0 !important; min-height: auto !important; margin: 0 auto !important; }
.access-report { position: relative !important; margin: 0 auto !important; background: #fff !important; }
.ctl { position: absolute !important; overflow: visible !important; line-height: 1.2 !important; }
.ctl.bold { font-weight: 700 !important; }
.ctl.align-center { text-align: center !important; }
.ctl.align-right { text-align: right !important; }
.ctl img { width: 100% !important; height: 100% !important; object-fit: contain !important; object-position: left top !important; display: block !important; }
.ctl.logo { overflow: hidden !important; }
`.trim()

/** Inject base href + PRINT_PREVIEW_CSS override + strip inline print button. */
function buildDoPreviewHtml(rawHtml: string): string {
  const withoutInlinePrint = rawHtml.replace(/<button\s+class="print-action[^>]*>[\s\S]*?<\/button>/g, '')
  const withBase = withoutInlinePrint.replace('<head>', `<head><base href="${window.location.origin}/">`)
  const styleTag = `<style id="do-print-preview-overrides">${PRINT_PREVIEW_CSS}</style>`
  if (withBase.includes('</head>')) return withBase.replace('</head>', `${styleTag}</head>`)
  return `${styleTag}${withBase}`
}

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

export default function DoPrintDialog({
  open,
  title,
  html,
  loading,
  canEdit = true,
  onClose,
}: DoPrintDialogProps) {
  const [toastMessage, setToastMessage] = useState('')
  const iframeTitle = 'DO Print Preview'
  const previewHtml = html ? buildDoPreviewHtml(html) : ''

  return (
    <>
      {open && loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog
        open={open && Boolean(html)}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={title}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            DO
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} size="small">
              Cancel
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                disabled={!html}
                onClick={() => {
                  const idPart = title.replace(/^.*\(([^)]+)\).*$/, '$1')
                  const printTitle = `${sanitizePdfFileNamePart(idPart || 'delivery_order')}_Ctrl_Sales_DO.pdf`
                  void printAndUploadReport({
                    iframeTitle,
                    reportName: 'rpCtrlSalesDO',
                    printTitle,
                    html: previewHtml,
                    onUploadError: setToastMessage,
                  })
                }}
              >
                Print
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  )
}
